import React, {Component} from 'react';
import {Route, HashRouter as Router, Switch, withRouter} from 'react-router-dom';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {has, get, toNumber} from 'lodash';
import {selectFeatureFlag} from '../selectors/featureFlagSelectors';
import {enums} from '@perion-undertone/ramp-helpers';
import {GAProvider, Button} from '@perion-undertone/ut-react-common';
import GoogleAnalyticsRouteListener from './GoogleAnalyticsRouteListener';
import _ from 'lodash';
import Sidebar from './Sidebar';
import routes from '../routes';
import {isAdmin, isPublisherUserOrPublisherAdmin} from '../auth';
import SurveyContainer from './survey/SurveyContainer';
import Image from './Image';
import PeriodDropdownSelect from './features/dashboard/PeriodDropdownSelect';
import {calendarPresets} from './calendarPresets';
import {activeItems, textProvider as dashboardTextProvider, getReplacedValue as dashboardGetReplacedValue, formatString as dashboardFormatString, setPeriod, getWidgetList, getWidgetLibrary} from './features/dashboard/imports';
import {saveToLocalStorage} from '../utils/localStorage';
import WidgetsLibraryModal from './features/dashboard/widgets/library/WidgetsLibraryModal';
import {getWidgetDefinitionsFromConfigData} from './features/dashboard/widgets/infra/widgetLoader';
import {
    routesHelper,
    appStates,
    formatString,
    textProvider,
    getReplacedValue,
    getCurrentPublisher,
    getAuthorizationData,
    SelectPublisher,
    SelectAllowedPublisher,
    getOnboardingData,
    SiteUnderReviewPage,
    OnboardingModal,
    RetryFetchData,
    fetchPublishers,
    redirectToHomePage,
    clearPredefinedReportData
} from './imports';
import {PUBLISHER_TYPES_MAP} from '../utils/globals';
import {
    isDevUiMockEnabled,
    parseDevUiMockPublisherId,
    getDevUiMockPublisher,
    applyDevUiRouteOverrides
} from '../utils/devUiMock';
import Header from "./Header";

const {PUBLISHER_ONBOARDING_STEPS} = enums;
const {SITE_UNDER_REVIEW} = PUBLISHER_ONBOARDING_STEPS;

const CLASS_NAMES = {
    HOME_LAYOUT: 'home-layout',
    HOME_BACKGROUND: 'home-background',
    MAIN_CONTAINER: 'main-container',
    CONTENT_WRAPPER: 'content-wrapper',
    CONTENT_WRAPPER_TITLE: 'content-wrapper-title',
    CONTENT_WRAPPER_TITLE_ICON_TEXT: 'content-wrapper-title-icon-text',
    SELECT_ALLOWED_PUBLISHER_WRAPPER: 'publisher-search-holder centered allowed-publishers',
    CONTENT_WRAPPER_TITLE_TEXT: 'content-wrapper-title-text'
};

const CONSTANT_TEXTS = {
    LOGIN_CATEGORY: textProvider.getText('ga', 'category.login'),
    CLICKED_LOGIN: textProvider.getText('ga', 'actions.clickedLogin'),
    PUBLISHER_PLATFORM: textProvider.getText('ga', 'category.publisherPlatform'),
    SELECTED_MODULE_FORMAT: textProvider.getText('ga', 'actions.selectedModuleFormat'),
    PUBLISHER_DASHBOARD_DATE_PICKER_CATEGORY: dashboardTextProvider.getText('ga', 'category.publisherDashboardDatePicker'),
    PREDEFINE_REPORT_FORMAT: dashboardTextProvider.getText('ga', 'actions.predefineReportFormat'),
    DROPDOWN_CUSTOM: dashboardTextProvider.getText('dropdownOptions', 'custom'),
    WIDGET_GALLERY: dashboardTextProvider.getText('widgetsLibraryModal', 'widgetGallery')
};

const HomeLayout = (props) => {
    return (
        <div className={CLASS_NAMES.HOME_LAYOUT}>
            <div className={CLASS_NAMES.HOME_BACKGROUND} />
            <Header isStagingEnv={props.isStagingEnv} />
            {props.children}
        </div>
    );
}

HomeLayout.propTypes = {
    isStagingEnv: PropTypes.bool
};

class Home extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dashboardActiveItem: activeItems['custom'],
            openWidgetsLibraryModal: false
        };
    }

    componentDidMount() {
        const {user} = this.props;
        redirectToHomePage();
        this.loadAuthorizationData();
        if (has(user, 'loginError') && !user.loginError) {
            GAProvider.notifyEvent(CONSTANT_TEXTS.LOGIN_CATEGORY, CONSTANT_TEXTS.CLICKED_LOGIN, getReplacedValue(user.data.name));
        }
    }

    componentDidUpdate() {
        const {user, auth, onboarding, isOnboardingEnabled, publishers, fetchPublishers, predefinedReportData} = this.props;
        if (auth && auth.data) {
            this.loadPublisherDetails();

            if(user && user.data && isPublisherUserOrPublisherAdmin(user.data) && !publishers) {
                fetchPublishers({publisherId: user.data.allowedPublisherId});
            }
        }
        if (isOnboardingEnabled && !onboarding && get(user, 'data.onBoardingCompleted') === '0') {
            this.props.getOnboardingData();
        }
        if (predefinedReportData && predefinedReportData.data && predefinedReportData.exportType !== 'web') {
            const {fileBuf, fileName} = predefinedReportData.data;
            fileBuf && this.downloadFileFromBuffer(fileBuf.data, fileName);
        }
        if (has(user, 'loginError') && !user.loginError) {
            GAProvider.notifyEvent(CONSTANT_TEXTS.LOGIN_CATEGORY, CONSTANT_TEXTS.CLICKED_LOGIN, getReplacedValue(user.data.name));
        }
    }

    loadAuthorizationData() {
        this.props.getAuthorizationData();
    }

    loadPublisherDetails() {
        const user = this.props.user.data;
        const {publisher, getCurrentPublisher, userUpdateState} = this.props;

        if (userUpdateState) return;

        let publisherId;
        if (isAdmin(user)) {
            const hash = window.location.hash;
            const hashParams = hash.split('/');
            publisherId = toNumber(hashParams[1]);
        }
        if (!isAdmin(user)) {
            publisherId = toNumber(user.currentPublisherId);
        }

        if (!publisherId) return;

        const publisherData = publisher && publisher.data;
        if (!publisher || (publisherData && _.parseInt(publisherData.id) !== _.parseInt(publisherId))) {
            getCurrentPublisher(publisherId);
        }
    }

    locationChanged = (location) => {
        const {user} = this.props;
        const splitRoute = location.pathname.split('/');
        if (splitRoute.length > 2) {
            GAProvider.notifyEvent(CONSTANT_TEXTS.PUBLISHER_PLATFORM, formatString(CONSTANT_TEXTS.SELECTED_MODULE_FORMAT, splitRoute[2]), getReplacedValue(user.data.name));
        }
    };

    downloadFileFromBuffer(content, name) {
        const base64data = Buffer.from(content, 'binary');
        const url = window.URL.createObjectURL(new Blob([base64data], {type: 'application/json', encoding: 'UTF-8'}));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.props.clearPredefinedReportData();
        // this.setState({isDownloading: false});
    }

    onSelectPredefinedItem = (event, eventKey, dropdownProps) => {
        const {user, setPeriod} = this.props;
        const currentOptionValue = dropdownProps.children[eventKey].props.value;
        const activePreset = calendarPresets.find(preset =>  preset.period === currentOptionValue);
        const presetPeriod = {
            type: currentOptionValue,
            from: activePreset.startDate,
            to: activePreset.endDate
        }
        setPeriod(presetPeriod);
        saveToLocalStorage("defaultWidgetPeriod", JSON.stringify(presetPeriod))
        this.setState({
            dashboardActiveItem: activeItems[currentOptionValue]
        });
        if (activeItems[currentOptionValue] !== this.state.dashboardActiveItem) {
            GAProvider.notifyEvent(CONSTANT_TEXTS.PUBLISHER_DASHBOARD_DATE_PICKER_CATEGORY,
                dashboardFormatString(CONSTANT_TEXTS.PREDEFINE_REPORT_FORMAT, activeItems[currentOptionValue]), dashboardGetReplacedValue(user.data.name))
        }
    };

    onSelectCustomItem = () => {
        this.setState({
            dashboardActiveItem: activeItems['custom']
        });
    }

    onPeriodChange = (periodFrom, periodTo) => {
        const {setPeriod} = this.props;
        const period = {
            type: 'custom',
            from: periodFrom,
            to: periodTo
        }
        setPeriod(period);
        this.onSelectCustomItem();
    }

    onDatePickerOpen = (isOpen) => {
        const {user} = this.props;
        if(!isOpen){
            GAProvider.notifyEvent(CONSTANT_TEXTS.PUBLISHER_DASHBOARD_DATE_PICKER_CATEGORY,
                dashboardFormatString(CONSTANT_TEXTS.PREDEFINE_REPORT_FORMAT, CONSTANT_TEXTS.DROPDOWN_CUSTOM), dashboardGetReplacedValue(user.data.name));
        }
    };

    onLibraryModalOpen = () => {
        this.setState({openWidgetsLibraryModal: true});
        localStorage.removeItem('newlyAddedLibraryWidget');
    }

    onLibraryModalClose = () => {
        this.setState({openWidgetsLibraryModal: false});
    }

    getWidgets = () => getWidgetDefinitionsFromConfigData(get(this.props, 'widgetPreferences.data'))

    rerenderWidgets = () => {
        // This method is passed to WidgetsLibraryModal but doesn't need to do anything in Home
        // The Dashboard component will handle rerendering when widgets change
    }

    render() {
        const {user, features, publisher: publisherProp, auth, location, isOnboardingEnabled, hideSiteUnderReviewPage,
            shouldDisplayEnterSite, onboarding, userUpdateState, paymentTab, publisherCountry, isStagingEnv} = this.props;

        let publisher = publisherProp;
        if (isDevUiMockEnabled()) {
            const mockId = parseDevUiMockPublisherId();
            if (!publisher || publisher.state === appStates.LOADING || publisher.state === appStates.ERROR || !publisher.data) {
                publisher = getDevUiMockPublisher(mockId);
            }
        }

        const hash = window.location.hash;
        const hashParams = hash.split('/');

        // Admin homepage
        if (!publisher && !toNumber(hashParams[1]) && isAdmin(user.data)) {
            return (
                <HomeLayout isStagingEnv={isStagingEnv}>
                    <SelectPublisher />
                </HomeLayout>
            );
        }

        // Direct user homepage, no current publisher
        const currentPublisherId = user.data.currentPublisherId;
        if (!currentPublisherId && isPublisherUserOrPublisherAdmin(user.data)) {
            return (
                <HomeLayout isStagingEnv={isStagingEnv}>
                    <div className={CLASS_NAMES.SELECT_ALLOWED_PUBLISHER_WRAPPER}>
                        <SelectAllowedPublisher />
                    </div>
                </HomeLayout>
            );
        }

        if (!publisher || (publisher.state === appStates.LOADING)) {
            return <HomeLayout isStagingEnv={isStagingEnv} />;
        }

        if (!userUpdateState && (publisher.state === appStates.ERROR || (auth && auth.state === appStates.ERROR))) {
            return (
                <HomeLayout isStagingEnv={isStagingEnv}>
                    <RetryFetchData classNames='centered' />
                </HomeLayout>
            );
        }

        const publisherId = publisher.data.id;
        const publisherTypeCd = PUBLISHER_TYPES_MAP[publisher.data.publisherTypeId];
        const filteredRoutes = isDevUiMockEnabled()
            ? applyDevUiRouteOverrides(routes)
            : routesHelper(routes, features, publisherId, user, {paymentTab}, publisherTypeCd, publisherCountry);

        if (isOnboardingEnabled && !hideSiteUnderReviewPage && !shouldDisplayEnterSite &&
            get(user, 'data.onBoardingStep') === SITE_UNDER_REVIEW && onboarding.state === appStates.LOADED) {
            return (
                <HomeLayout isStagingEnv={isStagingEnv}>
                    <div className={CLASS_NAMES.MAIN_CONTAINER}>
                        <Sidebar publisherId={publisherId}
                                 filteredRoutes={filteredRoutes}
                                 disabledMenu
                                 auth={auth}
                                 user={user}
                        />
                        <div className={CLASS_NAMES.CONTENT_WRAPPER}>
                            <SiteUnderReviewPage />
                        </div>
                    </div>
                </HomeLayout>
            );
        }

        return (
            <HomeLayout isStagingEnv={isStagingEnv}>
                <Router>
                    <div className={CLASS_NAMES.MAIN_CONTAINER}>
                        <Sidebar publisherId={publisherId}
                                 filteredRoutes={filteredRoutes}
                                 auth={auth}
                                 user={user}
                        />
                        <div className={CLASS_NAMES.CONTENT_WRAPPER}>
                            {isDevUiMockEnabled() ? (
                                <div
                                    style={{
                                        background: '#fff3cd',
                                        color: '#664d03',
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        borderBottom: '1px solid #e0c766',
                                        marginBottom: 8
                                    }}
                                >
                                    Dev UI mock: publisher and menu permissions are bypassed. Disable with{' '}
                                    <code>localStorage.removeItem(&apos;ut_dev_ui_mock&apos;)</code> then reload.
                                </div>
                            ) : null}
                            <Switch>
                                {filteredRoutes.map((route, index) => {
                                    return route.isEnabled && (
                                        <Route key={index}
                                               path={route.path}
                                               exact={route.exact}
                                                render={() => (
                                                   <>
                                                       <div className={CLASS_NAMES.CONTENT_WRAPPER_TITLE}>
                                                            <div className={CLASS_NAMES.CONTENT_WRAPPER_TITLE_ICON_TEXT}>
                                                                <Image name={route.imgWhite} hookId={`${route.url.slice(1)}-title-icon`} />
                                                                <div className={CLASS_NAMES.CONTENT_WRAPPER_TITLE_TEXT}>{route.displayName}</div>
                                                            </div>
                                                           {route.url === '/dashboard' && (
                                                               <div className="dashboard-actions">
                                                                   <PeriodDropdownSelect onSelectPredefinedItem={this.onSelectPredefinedItem}
                                                                                         activeItem={this.state.dashboardActiveItem}
                                                                                         period={this.props.period}
                                                                                         onDatePickerOpen={this.onDatePickerOpen}
                                                                                         onPeriodChange={this.onPeriodChange}
                                                                                         setPeriod={this.props.setPeriod}
                                                                   />
                                                                   {this.props.isWidgetLibraryEnabled &&
                                                                       <Button className="widget-library-button"
                                                                               icon="icon-library"
                                                                               iconPosition="left"
                                                                               onClick={this.onLibraryModalOpen}
                                                                               hookId="widget-library-button"
                                                                       >
                                                                           {CONSTANT_TEXTS.WIDGET_GALLERY}
                                                                       </Button>}
                                                               </div>
                                                           )}
                                                       </div>
                                                       <Route key={index}
                                                            path={route.path}
                                                            exact={route.exact}
                                                            component={route.component}
                                                        />
                                                   </>
                                               )}
                                        />
                                    );
                                })}
                            </Switch>
                        </div>
                    </div>
                </Router>
                <GoogleAnalyticsRouteListener locationChanged={this.locationChanged} location={location}/>
                {(shouldDisplayEnterSite || isOnboardingEnabled && get(user, 'data.onBoardingCompleted') === '0')
                && <OnboardingModal />}
                <SurveyContainer />
                {this.props.isWidgetLibraryEnabled && (
                    <WidgetsLibraryModal
                        openModal={this.state.openWidgetsLibraryModal}
                        closeModal={this.onLibraryModalClose}
                        closeOnOutsideClick={false}
                        widgetLibraryData={this.props.widgetLibrary}
                        widgets={this.getWidgets()}
                        rerenderWidgets={this.rerenderWidgets}
                    />
                )}
            </HomeLayout>
        );
    }
}

Home.propTypes = {
    features: PropTypes.array,
    history: PropTypes.object,
    user: PropTypes.object,
    publisher: PropTypes.object,
    publishers: PropTypes.object,
    getCurrentPublisher: PropTypes.func,
    fetchPublishers: PropTypes.func,
    userUpdateState: PropTypes.string,
    getAuthorizationData: PropTypes.func,
    getOnboardingData: PropTypes.func,
    onboarding: PropTypes.object,
    paymentTab: PropTypes.bool,
    isOnboardingEnabled: PropTypes.bool,
    hideSiteUnderReviewPage: PropTypes.bool,
    shouldDisplayEnterSite: PropTypes.bool,
    auth: PropTypes.object,
    predefinedReportData: PropTypes.object,
    clearPredefinedReportData: PropTypes.func,
    publisherCountry: PropTypes.string,
    isStagingEnv: PropTypes.bool,
    period: PropTypes.object,
    setPeriod: PropTypes.func,
    isWidgetLibraryEnabled: PropTypes.bool,
    widgetLibrary: PropTypes.object,
    widgetPreferences: PropTypes.object
};

const mapStateToProps = (state) => ({
    user: state.app.user,
    publisher: state.app.publisher,
    publishers: state.app.publishers,
    features: state.features,
    auth: state.app.auth,
    onboarding: state.app.onboarding,
    hideSiteUnderReviewPage: state.app.hideSiteUnderReviewPage,
    shouldDisplayEnterSite: state.app.shouldDisplayEnterSite,
    userUpdateState: state.app.userUpdateState,
    isOnboardingEnabled: selectFeatureFlag('publishersOnboarding')(state),
    predefinedReportData: state.predefinedReports.predefinedReportData,
    paymentTab: selectFeatureFlag('paymentTab')(state),
    publisherCountry: _.get(state, 'app.publisher.data.country'),
    isStagingEnv: selectFeatureFlag('isStagingEnv')(state),
    period: state.dashboard.period,
    isWidgetLibraryEnabled: selectFeatureFlag('widgetLibrary')(state),
    widgetLibrary: state.dashboard.widgetLibrary,
    widgetPreferences: state.dashboard.widgetPreferences
});

const mapDispatchToProps = {
    getCurrentPublisher,
    getAuthorizationData,
    getOnboardingData,
    fetchPublishers,
    clearPredefinedReportData,
    setPeriod,
    getWidgetList,
    getWidgetLibrary
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));
